import { graphql } from 'react-relay';
import type { ReactNode } from 'react';
import React, { useEffect, useContext } from 'react';
import { css, styled } from '@compiled/react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Anchor, xcss } from '@atlaskit/primitives';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	ExperienceStart,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ONBOARDING_TEMPLATE_COLLECTIONS_CREATE_TEMPLATE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import type { ExperienceAttributes } from '@confluence/experience-tracker/entry-points/ExperienceEvent.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { useCreateContentFromTemplate } from '@confluence/create-content-from-template/entry-points/useCreateContentFromTemplate.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useSetOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { TEMPLATES_COLLECTION } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	TemplateCardHomeViewGetTemplateInfoQuery as TemplateCardHomeViewGetTemplateInfoQuery$data,
	TemplateCardHomeViewGetTemplateInfoQueryVariables as TemplateCardHomeViewGetTemplateInfoQuery$variables,
	TemplateCardHomeViewGetTemplateInfoQuery_templateInfo_TemplateInfo as TemplateInfoType,
} from './__types__/TemplateCardHomeViewGetTemplateInfoQuery.ts';
import { TemplateIcon } from './TemplateIcon.tsx';
import { useCreateFromTemplateAnalyticsEvents } from './useCreateFromTemplateAnalyticsEvents.ts';
import WhiteboardsIcon from './images/WhiteboardsIcon.svg';
const i18nBase = defineMessages({
	secondary: {
		id: 'home.views.create-from-templates.template-card.secondary',
		defaultMessage: 'Template',
		description:
			'The text is shown as a secondary label beneath the template name on a template card in the Create from Template section of the Home page, indicating the card type is a template.',
	},
	whiteboardSecondary: {
		id: 'home.views.create-from-templates.whiteboard-card.secondary',
		defaultMessage: 'Choose a template',
		description:
			'The text is shown as a secondary label on the whiteboard card in the Create from Template section of the Home page, prompting the user to select a whiteboard template to create from.',
	},
	whiteboardPrimary: {
		id: 'home.views.create-from-templates.whiteboard-card.primary',
		defaultMessage: 'Create a whiteboard',
		description: 'Primary text for a whiteboard template card',
	},
	errorTitle: {
		id: 'home.views.create-from-templates.template-card.error-title',
		defaultMessage: 'We can’t create a page for you',
		description: 'Error flag title displayed when page creation from template fails',
	},
	errorDescription: {
		id: 'home.views.create-from-templates.template-card.error-description',
		defaultMessage: 'Refresh and try again. If this keeps happening, contact Support.',
		description:
			'Error flag description providing instructions when page creation from template fails',
	},
});
const i18nLiveDocs = defineMessages({
	errorTitle: {
		id: 'home.views.create-from-templates.template-card.error-title.livedocs',
		defaultMessage: 'We can’t create a page or live doc for you',
		description: 'Updated version of home.views.create-from-templates.template-card.error-title',
	},
});
const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Card = styled.div({
	position: 'relative',
	cursor: 'pointer',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface'),
	padding: token('space.200'),
	'&:hover': {
		boxShadow: token('elevation.shadow.raised'),
	},
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.div({
	display: 'flex',
	marginBottom: token('space.150'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardIcon = styled.div({
	marginRight: token('space.075'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardPrimarySecondary = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	flexShrink: 1,
	minWidth: 0,
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardPrimary = styled.div({
	color: token('color.text'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.heading.xsmall'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardSecondary = styled.div({
	color: token('color.text.subtle'),
	font: token('font.body.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	a: {
		color: token('color.text.subtle'),
		'&:visited': {
			color: token('color.text.subtle'),
		},
		'&:hover': {
			color: token('color.text.subtle'),
		},
	},
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CardPlaceholder = styled.div({
	display: 'flex',
	height: '62px',
	padding: token('space.200'),
	flexDirection: 'column',
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentPlaceholder = styled.div({
	display: 'flex',
	marginBottom: token('space.200'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconPlaceholder = styled.div({
	width: '24px',
	height: '24px',
	marginTop: token('space.025'),
	marginRight: token('space.200'),
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleAndTypePlaceholder = styled.div({
	display: 'flex',
	flexDirection: 'column',
	flexGrow: 1,
	flexShrink: 1,
	justifyContent: 'space-between',
	minWidth: '200px',
	marginRight: token('space.150'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitlePlaceholder = styled.div({
	height: '12px',
	width: '35%',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
	marginBottom: token('space.025'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TypePlaceholder = styled.div({
	height: '12px',
	width: '20%',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionPlaceholder = styled.div({
	height: '12px',
	width: '60%',
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage -- The token value "4px" and fallback "3px" do not match and can't be replaced automatically.
	borderRadius: token('radius.small', '3px'),
});
const whiteboardContainerStyle = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	textDecoration: 'none !important',
});

const whiteboardListStyle = css({
	textDecoration: 'none',
	margin: 0,
});

const templateListStyle = css({
	listStyle: 'none',
	padding: 0,
	margin: 0,
});

export type TemplateCardProps = {
	secondary: ReactNode;
	templateKey: string;
	personalSpaceKey: string;
	teamTypeLoading: boolean;
	onRenderingDone: () => void;
};
type CardDateType = Pick<TemplateInfoType, 'name' | 'iconURL' | 'darkModeIconURL'>;
type CardType = {
	onClick?: (() => Promise<void>) | (() => void);
	data: CardDateType | null | undefined;
	secondaryMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	secondary: ReactNode;
	loading?: boolean;
};
const reportError = (
	experienceTracker: ExperienceTrackerAPI,
	error: Error,
	attributes?: ExperienceAttributes,
): void => {
	markErrorAsHandled(error);
	experienceTracker.fail({
		name: ONBOARDING_TEMPLATE_COLLECTIONS_CREATE_TEMPLATE_EXPERIENCE,
		error,
		attributes,
	});
};
const CardComponent = ({ onClick, data, secondaryMessage, secondary, loading }: CardType) => {
	const { name, iconURL, darkModeIconURL } = data ?? {};
	return !loading && data ? (
		<Card data-testid="template-card" onClick={onClick}>
			<ContentWrapper>
				<Header>
					<CardIcon data-testid="template-card-icon">
						<TemplateIcon iconURL={iconURL || ''} darkModeIconURL={darkModeIconURL || ''} />
					</CardIcon>
					<CardPrimarySecondary>
						<CardPrimary>{name}</CardPrimary>
						<CardSecondary>
							<FormattedMessage {...secondaryMessage} />
						</CardSecondary>
					</CardPrimarySecondary>
				</Header>
				<CardSecondary>{secondary}</CardSecondary>
			</ContentWrapper>
		</Card>
	) : (
		<CardPlaceholder data-testid="template-card-skeleton">
			<ContentPlaceholder>
				<IconPlaceholder />
				<TitleAndTypePlaceholder>
					<TitlePlaceholder />
					<TypePlaceholder />
				</TitleAndTypePlaceholder>
			</ContentPlaceholder>
			<DescriptionPlaceholder />
		</CardPlaceholder>
	);
};
export const TemplateCard = withFlags<TemplateCardProps>(
	({ secondary, templateKey, personalSpaceKey, onRenderingDone, flags, teamTypeLoading }) => {
		const { loading, data, error } = useQueryInterop<
			TemplateCardHomeViewGetTemplateInfoQuery$data,
			TemplateCardHomeViewGetTemplateInfoQuery$variables
		>(
			graphql`
				query TemplateCardHomeViewGetTemplateInfoQuery($blueprintKey: ID!) {
					templateInfo(id: $blueprintKey) {
						name
						iconURL
						darkModeIconURL
						templateType
						contentBlueprintId
						blueprintModuleCompleteKey
						itemModuleCompleteKey
					}
				}
			`,
			{
				variables: {
					blueprintKey: templateKey,
				},
				onCompleted: () => {
					onRenderingDone();
				},
				onError: (error) => {
					reportError(experienceTracker, error, { blueprintKey: templateKey });
					// Here we don't show a flag, because the user has not yet taken any action.
				},
				skip: teamTypeLoading,
			},
		);
		const experienceTracker = useContext(ExperienceTrackerContext);
		const createFromTemplate = useCreateContentFromTemplate();
		const { setOnboardingState } = useSetOnboardingState();
		const sendCreateFromTemplateAnalyticsEvent = useCreateFromTemplateAnalyticsEvents();
		const renderServerPlaceholder = useRenderServerPlaceholder();

		const handleOnClick = async () => {
			const template = data?.templateInfo;
			if (!template) {
				return;
			}
			const handleCreationError = (error: Error) => {
				reportError(experienceTracker, error, { blueprintKey: templateKey });
				void flags.showErrorFlag({
					title: <FormattedMessage {...i18n.errorTitle} />,
					description: <FormattedMessage {...i18n.errorDescription} />,
				});
			};
			sendCreateFromTemplateAnalyticsEvent({
				type: 'sendUIEvent',
				action: 'clicked',
				actionSubject: 'component',
				actionSubjectId: 'createPageFromTemplate',
				attributes: {
					templateKey: template.blueprintModuleCompleteKey,
					spaceKey: personalSpaceKey,
				},
			});
			const createOptions = {
				onVariableInputError: handleCreationError,
				forceTemplatePanelOpen: true,
			};
			try {
				await createFromTemplate(template, personalSpaceKey, createOptions);
				experienceTracker.succeed({
					name: ONBOARDING_TEMPLATE_COLLECTIONS_CREATE_TEMPLATE_EXPERIENCE,
				});
				void setOnboardingState({
					key: TEMPLATES_COLLECTION.EXPERIMENT_DISMISSED,
					value: 'true',
				});
			} catch (err) {
				handleCreationError(err);
			}
		};

		if (error) {
			markErrorAsHandled(error);
			return <ErrorDisplay error={error} />;
		}

		if (!renderServerPlaceholder && !loading && !teamTypeLoading && !data?.templateInfo) {
			return null;
		}

		return (
			<>
				<ExperienceStart
					name={ONBOARDING_TEMPLATE_COLLECTIONS_CREATE_TEMPLATE_EXPERIENCE}
					attributes={{ blueprintKey: templateKey }}
				/>
				<li css={templateListStyle}>
					<CardComponent
						onClick={handleOnClick}
						loading={loading}
						data={data?.templateInfo}
						secondary={secondary}
						secondaryMessage={i18n.secondary}
					/>
				</li>
			</>
		);
	},
);
export const WhiteboardCard = ({
	secondary,
	templateKey,
	personalSpaceKey,
	teamTypeLoading,
}: TemplateCardProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const sendCreateFromTemplateAnalyticsEvent = useCreateFromTemplateAnalyticsEvents();
	const SCREEN_NAME = 'homeScreenCreateFromTemplate';
	const createWhiteboardHref = buildCreationLink({
		contentType: 'whiteboard',
		source: SCREEN_NAME,
		withFallback: true,
		spaceKey: personalSpaceKey,
	});
	const intl = useIntl();
	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'whiteboardCard',
				source: 'homeOnboarding',
			},
		}).fire();
	}, [createAnalyticsEvent]);
	const onClick = () => {
		sendCreateFromTemplateAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'component',
			actionSubjectId: 'whiteboardCard',
			attributes: {
				templateKey,
				spaceKey: personalSpaceKey,
			},
		});
		experienceTracker.succeed({
			name: ONBOARDING_TEMPLATE_COLLECTIONS_CREATE_TEMPLATE_EXPERIENCE,
		});
	};
	return (
		<>
			<ExperienceStart
				name={ONBOARDING_TEMPLATE_COLLECTIONS_CREATE_TEMPLATE_EXPERIENCE}
				attributes={{ blueprintKey: templateKey }}
			/>
			<li css={whiteboardListStyle}>
				<Anchor xcss={whiteboardContainerStyle} href={createWhiteboardHref}>
					<CardComponent
						data={{
							name: intl.formatMessage(i18n.whiteboardPrimary),
							iconURL: WhiteboardsIcon,
							darkModeIconURL: WhiteboardsIcon,
						}}
						secondaryMessage={i18n.whiteboardSecondary}
						secondary={secondary}
						onClick={onClick}
						loading={teamTypeLoading}
					/>
				</Anchor>
			</li>
		</>
	);
};
